/**
 * BecomeAMemberSection component handles membership registration forms
 * Supports both "short" and "full" form variants based on the data.type prop
 * Short form includes essential fields only, while full form includes all registration details
 * Integrates with TeamLeader API for provinces and processes membership creation
 */
"use client";

import { useEffect, useState } from "react";

import RichText from "@/components/general/nsc/RichText";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import DynamicForm from "@/components/general/DynamicForm";
import { getProvinces } from "@/lib/api/teamleader";
import { reverseDateFormat } from "@/lib/utils";
import { toast } from "sonner";
import { useRouter } from "next/navigation";
import { getShortFormFields, getFullFormFields, FormField } from "@/lib/membershipFormFields";

const BecomeAMemberSection = ({ data }: any) => {
  const router = useRouter();
  const [formFields, setFormFields] = useState<FormField[]>([]);

  useEffect(() => {
    getProvinces().then((provincesData) => {
      // Set form fields based on the type prop
      if (data?.type === "short") {
        setFormFields(getShortFormFields());
      } else {
        setFormFields(getFullFormFields(provincesData.data));
      }
    });
  }, [data?.type]);

  const handleSubmit = async (formData: FormData) => {
    const rawFormData: any = {
      email: formData.get("email"),
      firstName: formData.get("firstName"),
      lastName: formData.get("lastName"),
      terms: formData.get("terms") === "on" ? true : false,
      marketingMailsConsent: formData.get("newsletter") === "on" ? true : false,
      type: data?.type, // Pass the type of the form to the backend
    };

    // Add full form fields only if they exist (for full form type)
    if (data?.type !== "short") {
      const mobileNumber = formData.get("mobilePhone");
      rawFormData.membershipType = formData.get("membershipType");
      rawFormData.gender = formData.get("gender");
      rawFormData.street = formData.get("street");
      rawFormData.houseNumber = formData.get("houseNumber");
      rawFormData.postalCode = formData.get("postalCode");
      rawFormData.birthdate = reverseDateFormat(formData.get("birthdate"));
      rawFormData.city = formData.get("city");
      rawFormData.areaLevelTwoId = formData.get("province");
      rawFormData.mobileNumber = mobileNumber;
      rawFormData.phoneNumber = formData.get("phoneNumber");
      rawFormData.municipality = formData.get("municipality");
      rawFormData.contact = formData.get("contact") === "on" ? true : false;
    } else {
      rawFormData.ageConfirmation = formData.get("ageConfirmation") === "on" ? true : false;
    }

    await fetch(`${`/api/auth/create-account`}`, {
      method: "POST",
      body: JSON.stringify(rawFormData),
    })
      .then((response) => {
        if (response.ok) {
          router.push("/mijn-account/inloggen/account-created");
        } else {
          toast.error("Er is een fout opgetreden. Probeer het later nog eens.");
        }
      })
      .catch((error) => {
        console.log(error);
        toast.error("Er is een fout opgetreden. Probeer het later nog eens.");
      });
  };

  // Only render border-b if there are FAQ items
  const hasFAQItems = data?.faq?.items && data?.faq?.items?.length > 0;

  return (
    <section className="py-16 md:py-32">
      <div className="grid-container">
        <div className="order-first col-span-12 md:hidden">
          {data?.description && (
            <RichText data={data.description} className="rich-text mb-0 border-[rgba(23,28,96,0.4)] pb-8" />
          )}
        </div>
        <div className="col-span-12 mt-16 md:col-span-6 md:mt-0 lg:col-span-5">
          {data?.description && (
            <RichText
              data={data.description}
              className={`rich-text mb-0 hidden border-[rgba(23,28,96,0.4)] pb-8 md:block ${hasFAQItems ? "border-b" : "border-none"}`}
            />
          )}
          <Accordion type="single" collapsible className="w-full">
            {data?.faq?.items && data?.faq?.items?.length > 0 && (
              data?.faq?.items?.map((item: any) => (
                <AccordionItem key={item.id} value={item.id}>
                  <AccordionTrigger className="header-3 !border-none pb-8 data-[state=open]:pb-0">
                    {item.title}
                  </AccordionTrigger>
                  <AccordionContent>
                    {item?.description && <RichText data={item.description} className="rich-text" />}
                  </AccordionContent>
                </AccordionItem>
              ))
            )}
          </Accordion>
        </div>
        <div className="relative left-[-10px] order-first col-span-12 h-fit w-screen bg-nsc-dark-blue px-[10px] py-16 md:left-0 md:order-last md:col-span-6 md:col-start-7 md:w-full md:rounded md:p-10 lg:p-16">
          <h2 className="header-3 mb-8 text-white md:text-[42px]">{data?.formTitle}</h2>
          <DynamicForm
            fields={formFields}
            onSubmit={handleSubmit}
            defaultValues={
              data?.type === "full" ? { membershipType: "nsc" } : {}
            }
            buttonLabel="Ja, ik word lid"
            extraInfo={
              data?.type === "short" 
                ? "Overige informatie betreffende uw adres gegevens en dergelijke vragen wij later van u via email. Veilig en vertrouwd."
                : null
            }
            keepForm
            hasRequiredFields
          />
        </div>
      </div>
    </section>
  );
};

export default BecomeAMemberSection;
